/**
 * AI credit pricing math, shared by the API runtime (metering, settlement, output caps) and the
 * Super Admin pricing editor (live calculator), so both always compute the same numbers.
 *
 *   cost_micros(call) = input·r.input + cacheRead·r.cachedInput
 *                     + cacheWrite·r.input·cacheWriteMultiplier + output·r.output
 *   r = the model's long-prompt tier when THIS call's prompt tokens
 *       (input + cache read + cache write) > tier.thresholdInputTokens, else its base rates
 *   credits(run) = max(minimum(purpose), ceil(Σ cost_micros(call) / unit))
 *
 * Rates are micro-USD per token (= USD per 1M tokens). A run's cost is the SUM OF PER-CALL costs:
 * pricing summed token totals would apply the tier to a run of many short calls (or miss it on
 * one long call), so `callCostMicros` must only ever see one model call's usage.
 */
import type { AiCreditPolicy, AiModelRates, AiPurpose } from './contracts/ai';

export interface AiCallUsage {
  inputTokens: number;
  outputTokens: number;
  /** Cache read tokens. */
  cachedTokens: number;
  cacheWriteTokens: number;
}

export interface AiCallRates {
  input: number;
  output: number;
  cachedInput: number;
  /** True when the long-prompt tier applies. */
  longContext: boolean;
}

/** Prompt size of one call as the tier threshold counts it (input + cache read + cache write). */
export function callPromptTokens(u: Partial<AiCallUsage>): number {
  return (u.inputTokens || 0) + (u.cachedTokens || 0) + (u.cacheWriteTokens || 0);
}

export function hasExplicitModelRate(policy: AiCreditPolicy, model: string): boolean {
  return Object.prototype.hasOwnProperty.call(policy.modelRates, model);
}

/** The rate entry used for a model: its own, else `defaultRates`. */
export function modelRateEntry(policy: AiCreditPolicy, model: string): AiModelRates {
  return (
    (hasExplicitModelRate(policy, model) ? policy.modelRates[model] : undefined) ??
    policy.defaultRates
  );
}

/** Effective per-token rates for one call of `promptTokens` prompt tokens on a model. */
export function ratesForCall(
  policy: AiCreditPolicy,
  model: string,
  promptTokens: number,
): AiCallRates {
  const r = modelRateEntry(policy, model);
  const lc = r.longContext;
  if (lc && promptTokens > lc.thresholdInputTokens)
    return { input: lc.input, output: lc.output, cachedInput: lc.cachedInput, longContext: true };
  return { input: r.input, output: r.output, cachedInput: r.cachedInput, longContext: false };
}

/** Rates that are never lower than what a call on this model can be billed at (either tier). */
export function maxRatesForModel(policy: AiCreditPolicy, model: string): AiCallRates {
  const r = modelRateEntry(policy, model);
  const lc = r.longContext;
  if (!lc)
    return { input: r.input, output: r.output, cachedInput: r.cachedInput, longContext: false };
  return {
    input: Math.max(r.input, lc.input),
    output: Math.max(r.output, lc.output),
    cachedInput: Math.max(r.cachedInput, lc.cachedInput),
    longContext: true,
  };
}

/** Provider cost in micro-USD of ONE model call (unrounded parts, rounded total). */
export function callCostMicros(
  policy: AiCreditPolicy,
  model: string,
  u: Partial<AiCallUsage>,
): number {
  const r = ratesForCall(policy, model, callPromptTokens(u));
  return Math.round(
    (u.inputTokens || 0) * r.input +
      (u.cachedTokens || 0) * r.cachedInput +
      (u.cacheWriteTokens || 0) * r.input * policy.cacheWriteMultiplier +
      (u.outputTokens || 0) * r.output,
  );
}

export function purposeMinimumCredits(policy: AiCreditPolicy, purpose: AiPurpose): number {
  return policy.minimumByPurpose[purpose] ?? policy.defaultMinimum;
}

/** Credits for a run's total cost (never below the purpose minimum once any model call happened). */
export function creditsForMicros(
  policy: AiCreditPolicy,
  purpose: AiPurpose,
  micros: number,
  anyCall: boolean,
): number {
  if (!anyCall) return 0;
  return Math.max(purposeMinimumCredits(policy, purpose), Math.ceil(micros / policy.unit));
}

/**
 * Fixed `maxCredits` caps the AI features pass per run (one source of truth: the callers import
 * these). A run is rejected when its cap is below the purpose minimum, so the admin editor must
 * never set a minimum above the smallest cap of a purpose (`AI_PURPOSE_CREDIT_CAPS`).
 */
export const AI_CALLER_MAX_CREDITS = {
  chatTurn: 50,
  onboardingExtract: 3,
  onboardingPropose: 10,
  segmentation: 3,
  /** Lower bound of the tenant-configurable insights cap (`InsightsConfig.maxCreditsPerRun`). */
  insightsMinPerRun: 2,
  reviewReplyDraft: 4,
  campaignCopy: 3,
  /** One AI email design (or refinement) — block JSON, a few thousand output tokens. */
  emailDesign: 8,
  /** One AI form page design (or refinement) — blocks + fields JSON. */
  formDesign: 8,
  /** One question over a form's answers (≤ 3 000 output tokens, bounded pseudonymous dataset). */
  formInsights: 10,
  importMapping: 15,
  dashboardCodegen: 60,
} as const;

const C = AI_CALLER_MAX_CREDITS;
/** Smallest fixed credit cap a run of each purpose can have (the purpose minimum must fit in it). */
export const AI_PURPOSE_CREDIT_CAPS: Record<AiPurpose, number> = {
  general_chat: C.chatTurn,
  onboarding: Math.min(C.onboardingExtract, C.onboardingPropose),
  segmentation: C.segmentation,
  insights: C.insightsMinPerRun,
  review_reply: C.reviewReplyDraft,
  campaign_copy: C.campaignCopy,
  import_mapping: C.importMapping,
  email_design: C.emailDesign,
  form_design: C.formDesign,
  form_insights: C.formInsights,
  dashboard_codegen: C.dashboardCodegen,
};
